import { useQueryClient } from '@tanstack/react-query'
import { useHeatmapPages, useSavedFunnels } from '@/hooks/useAnalytics'
import { useSubscription } from '@/lib/contexts/SubscriptionContext'
import type { TrialUsageStats } from '@/lib/constants/pricing'

/**
 * Source the data-injection numbers for TrialBanner phase copy.
 *
 * Heatmaps + funnels are cheap list endpoints — we query them while a trial
 * is active. Visitor/session count piggybacks on the analytics cache rather
 * than forcing a fresh fetch from non-analytics pages; if the user hasn't
 * loaded the dashboard yet, that field is null and the banner falls back
 * to non-data copy. That is the intended degrade path (Paper spec §03).
 */
export function useTrialUsageStats(): TrialUsageStats & { isLoading: boolean } {
  const { hasActiveTrial, usedTrial } = useSubscription()
  const queryClient = useQueryClient()

  // A spent trial still names what it kept (expired banner + paywall).
  const enabled = hasActiveTrial || usedTrial
  const heatmapsQuery = useHeatmapPages()
  const funnelsQuery = useSavedFunnels()

  if (!enabled) {
    return { sessions_n: null, heatmaps_n: null, funnels_n: null, isLoading: false }
  }

  const heatmaps_n = Array.isArray(heatmapsQuery.data) ? heatmapsQuery.data.length : null
  const funnels_n = Array.isArray(funnelsQuery.data) ? funnelsQuery.data.length : null

  // Peek the analytics cache. We don't trigger the query — only read it
  // if a dashboard page has already populated it this session.
  const analyticsCaches = queryClient.getQueriesData<{ visitors?: number; sessions?: number }>({
    queryKey: ['analytics'],
  })
  let sessions_n: number | null = null
  for (const [, data] of analyticsCaches) {
    const candidate = data?.sessions ?? data?.visitors
    if (typeof candidate === 'number' && candidate > 0) {
      sessions_n = candidate
      break
    }
  }

  return {
    sessions_n,
    heatmaps_n,
    funnels_n,
    isLoading: heatmapsQuery.isLoading || funnelsQuery.isLoading,
  }
}
